import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import NavBar from './components/NavBar';
import MapPage from './pages/MapPage';
import LoginPage from './pages/LoginPage';
import PendingApprovalPage from './pages/PendingApprovalPage';
import AdminPage from './pages/AdminPage';
import SegmentsPage from './pages/SegmentsPage';
import WaiverPage from './pages/WaiverPage';
import ConcernsPage from './pages/ConcernsPage';
import MaintenanceRequestsPage from './pages/MaintenanceRequestsPage';
import MeetingSchedulerPage from './pages/MeetingSchedulerPage';
import PortalPage from './pages/PortalPage';
import PortalMeetingsPage from './pages/PortalMeetingsPage';
import PortalMeetingPage from './pages/PortalMeetingPage';
import PortalActionsPage from './pages/PortalActionsPage';
import RunMeetingPage from './pages/RunMeetingPage';
import ReviewMeetingPage from './pages/ReviewMeetingPage';

function ProtectedRoute({ children, requireAdmin: needAdmin, requireVolunteer }) {
  const { user, loading, isAdmin, isVolunteer } = useAuth();

  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (user.status === 'pending') return <Navigate to="/pending-approval" />;
  if (needAdmin && !isAdmin) return <Navigate to="/" />;
  if (requireVolunteer && !isVolunteer) return <Navigate to="/" />;

  return children;
}

export default function App() {
  const { user, loading } = useAuth();

  if (loading) return <div className="loading">Loading LCPOA Trails...</div>;

  return (
    <div className="app">
      {user && user.status === 'active' && <NavBar />}

      <Routes>
        {/* Public routes */}
        <Route path="/waiver" element={<WaiverPage />} />
        <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
        <Route path="/pending-approval" element={
          !user ? <Navigate to="/login" /> :
          user.status !== 'pending' ? <Navigate to="/" /> :
          <PendingApprovalPage />
        } />

        {/* Protected routes */}
        <Route path="/" element={<ProtectedRoute><MapPage /></ProtectedRoute>} />

        <Route path="/segments" element={
          <ProtectedRoute requireVolunteer><SegmentsPage /></ProtectedRoute>
        } />

        <Route path="/concerns" element={
          <ProtectedRoute requireVolunteer><ConcernsPage /></ProtectedRoute>
        } />

        <Route path="/maintenance" element={
          <ProtectedRoute requireVolunteer><MaintenanceRequestsPage /></ProtectedRoute>
        } />

        <Route path="/meetings" element={
          <ProtectedRoute requireVolunteer><MeetingSchedulerPage /></ProtectedRoute>
        } />

        {/* Volunteer Portal — meetings, agendas, minutes, action items */}
        <Route path="/volunteer" element={
          <ProtectedRoute requireVolunteer><PortalPage /></ProtectedRoute>
        } />

        <Route path="/volunteer/meetings" element={
          <ProtectedRoute requireVolunteer><PortalMeetingsPage /></ProtectedRoute>
        } />

        <Route path="/volunteer/meetings/:id" element={
          <ProtectedRoute requireVolunteer><PortalMeetingPage /></ProtectedRoute>
        } />

        <Route path="/volunteer/meetings/:id/run" element={
          <ProtectedRoute requireVolunteer><RunMeetingPage /></ProtectedRoute>
        } />

        <Route path="/volunteer/meetings/:id/review" element={
          <ProtectedRoute requireVolunteer><ReviewMeetingPage /></ProtectedRoute>
        } />

        <Route path="/volunteer/actions" element={
          <ProtectedRoute requireVolunteer><PortalActionsPage /></ProtectedRoute>
        } />

        <Route path="/admin" element={
          <ProtectedRoute requireAdmin><AdminPage /></ProtectedRoute>
        } />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}
